import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  loading: { en: 'Loading Rulebook…', hi: 'रूलबुक लोड हो रहा है…' },
  title: { en: 'Aayush Rulebook', hi: 'आयुष रूलबुक' },
  subtitle: { en: 'Editing here changes how Aayush behaves instantly — ', hi: 'यहाँ बदलाव करते ही Aayush नए नियमों से चलने लगता है — ' },
  no_deploy: { en: 'no deploy needed', hi: 'कोई deploy नहीं चाहिए' },
  customized: { en: 'Customized', hi: 'कस्टमाइज़्ड' },
  running_default: { en: 'Running default', hi: 'अभी default चल रहा है' },
  reset_default: { en: 'Reset to Default', hi: 'Default पर वापस' },
  saving: { en: 'Saving…', hi: 'सेव हो रहा…' },
  save_live: { en: '💾 Save & Go Live', hi: '💾 Save & Go Live' },
  reset_section: { en: 'Reset this section to default', hi: 'इस section को default करें' },
  chars: { en: 'characters', hi: 'characters' },
  saved: { en: '✅ Rulebook saved — live now!', hi: '✅ Rulebook सेव हो गया — अभी से live!' },
  save_failed: { en: 'Save failed', hi: 'Save failed' },
  confirm_reset: { en: 'Discard all custom changes and go back to the default rulebook?', hi: 'सारे custom बदलाव हटाकर default rulebook पर वापस जाएँ?' },
  reset_done: { en: '↩️ Reverted to default rulebook', hi: '↩️ Default rulebook पर वापस आ गए' },
  reset_failed: { en: 'Reset failed', hi: 'Reset failed' },
  sec_platform_label: { en: 'Platform Context', hi: 'प्लेटफ़ॉर्म संदर्भ' },
  sec_platform_hint: { en: 'Who Aayush is, platform info, pricing, contact. (Keep placeholders like {{BASE_PRICE}}, {{FOUNDING_PRICE}} — they auto-fill.)', hi: 'Aayush कौन है, platform की जानकारी, pricing, contact। ({{BASE_PRICE}}, {{FOUNDING_PRICE}} जैसे placeholders रहने दें — ये अपने-आप भर जाते हैं।)' },
  sec_memory_label: { en: 'Memory & Closing Rules', hi: 'मेमोरी व क्लोज़िंग नियम' },
  sec_memory_hint: { en: 'How to keep memory, when and how to close payment/demo (soft, not forceful).', hi: 'Memory कैसे रखे, payment/demo closing कब और कैसे करे (soft, forceful नहीं)।' },
  sec_qflow_label: { en: 'Question Flow Playbook', hi: 'प्रश्न प्रवाह प्लेबुक' },
  sec_qflow_hint: { en: 'In what order Aayush asks questions — discovery → qualification → demo/payment.', hi: 'Aayush किस क्रम में सवाल पूछे — discovery → qualification → demo/payment।' },
  sec_lead_label: { en: 'Lead Capture Instructions', hi: 'लीड कैप्चर निर्देश' },
  sec_lead_hint: { en: 'Rules for data tags ([DATA:...]), lead capture and extraction.', hi: 'Data tags ([DATA:...]), lead capture और extraction के नियम।' },
};

export default function AayushRulebookEditor({ token }) {
  const { language } = useLanguage();
  const tr = (k) => (T[k] ? T[k][language] || T[k].en : k);

  const SECTIONS = [
    { key: 'platform_context', label: tr('sec_platform_label'), hint: tr('sec_platform_hint') },
    { key: 'memory_closing_rules', label: tr('sec_memory_label'), hint: tr('sec_memory_hint') },
    { key: 'question_flow_playbook', label: tr('sec_qflow_label'), hint: tr('sec_qflow_hint') },
    { key: 'lead_capture_instructions', label: tr('sec_lead_label'), hint: tr('sec_lead_hint') },
  ];

  const [sections, setSections] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({});
  const [activeKey, setActiveKey] = useState('platform_context');
  const [toast, setToast] = useState('');

  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/admin/rulebook`, { headers: authHeaders });
      const data = await res.json();
      if (data.success) {
        setSections(data.sections);
        setDefaults(data.defaults || {});
        setMeta({ is_customized: data.is_customized, updated_at: data.updated_at, updated_by: data.updated_by });
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/admin/rulebook`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify(sections),
      });
      const data = await res.json();
      if (data.success) { showToast(tr('saved')); load(); }
      else showToast(data.detail || tr('save_failed'));
    } catch (e) { showToast(tr('save_failed')); }
    setSaving(false);
  };

  const resetAll = async () => {
    if (!window.confirm(tr('confirm_reset'))) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/admin/rulebook/reset`, { method: 'POST', headers: authHeaders });
      const data = await res.json();
      if (data.success) { showToast(tr('reset_done')); load(); }
    } catch (e) { showToast(tr('reset_failed')); }
    setSaving(false);
  };

  const resetSection = (key) => {
    if (defaults[key] !== undefined) setSections((s) => ({ ...s, [key]: defaults[key] }));
  };

  if (loading || !sections) {
    return <div className="p-8 text-gray-500" data-testid="rulebook-loading">{tr('loading')}</div>;
  }

  const active = SECTIONS.find((s) => s.key === activeKey);

  return (
    <div className="max-w-6xl mx-auto" data-testid="aayush-rulebook-editor">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <span>📘</span> {tr('title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {tr('subtitle')}<b>{tr('no_deploy')}</b>।
            {meta.is_customized ? (
              <span className="ml-1 text-emerald-600">{tr('customized')}{meta.updated_by ? ` · ${meta.updated_by}` : ''}</span>
            ) : (
              <span className="ml-1 text-gray-400">{tr('running_default')}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetAll} disabled={saving} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50" data-testid="rulebook-reset-all">
            {tr('reset_default')}
          </button>
          <button onClick={save} disabled={saving} className="px-5 py-2 text-sm font-bold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50" data-testid="rulebook-save">
            {saving ? tr('saving') : tr('save_live')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-1 space-y-1">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveKey(s.key)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition ${activeKey === s.key ? 'bg-orange-100 text-orange-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}
              data-testid={`rulebook-tab-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="md:col-span-3 bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-slate-800">{active.label}</h2>
            <button onClick={() => resetSection(active.key)} className="text-xs text-gray-400 hover:text-orange-600" data-testid={`rulebook-reset-${active.key}`}>
              {tr('reset_section')}
            </button>
          </div>
          <p className="text-xs text-gray-400 mb-3">{active.hint}</p>
          <textarea
            value={sections[active.key] || ''}
            onChange={(e) => setSections((s) => ({ ...s, [active.key]: e.target.value }))}
            className="w-full h-[55vh] font-mono text-xs leading-relaxed border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-orange-400 focus:outline-none resize-none"
            spellCheck={false}
            data-testid={`rulebook-textarea-${active.key}`}
          />
          <p className="text-[11px] text-gray-400 mt-2">{(sections[active.key] || '').length} {tr('chars')}</p>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 bg-slate-900 text-white px-4 py-2 rounded-xl text-sm shadow-lg z-50" data-testid="rulebook-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
